import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, DatePicker, Segmented, Progress, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined, PlayCircleOutlined, CheckOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { label } from '../../lib/format';
export const TASK_STATUS_COLORS = { OPEN: 'blue', WORKING: 'gold', COMPLETED: 'green', CANCELLED: 'default' };
export const PRIORITY_COLORS = { LOW: 'default', MEDIUM: 'blue', HIGH: 'red' };
const VIEWS = ['Open', 'My tasks', 'Overdue', 'Completed', 'All'];
const today = () => moment().format('YYYY-MM-DD');

export function ChecklistProgress({ items }) {
  const total = (items || []).length;
  if (!total) return <Typography.Text type="secondary">-</Typography.Text>;
  const done = items.filter((i) => i.is_done).length;
  return <Progress percent={Math.round((done / total) * 100)} size="small" format={() => `${done}/${total}`} style={{ width: 110, marginBottom: 0 }} />;
}

export function DueDate({ task }) {
  if (!task.exp_end_date) return '-';
  const overdue = !['COMPLETED', 'CANCELLED'].includes(task.status) && task.exp_end_date < today();
  return <span style={{ color: overdue ? '#cf1322' : undefined, fontWeight: overdue ? 600 : undefined }}>{task.exp_end_date}</span>;
}

/** Quick Start / Complete on a row; the detail page has the full set (cancel, reopen, force-complete). */
export function quickStatus(task, status, onDone) {
  return request
    .post(`/tasks/${task.id}/status`, { status })
    .then((r) => r.json())
    .then((r) => {
      if (r.status) {
        notification.success({ message: `${task.subject} - ${status.toLowerCase()}` });
        onDone(r.data);
      } else {
        notification.warning({ message: r.message });
      }
      return r;
    });
}

/** The firm's work list: every Task across projects, filtered by view / assignee. */
export default function TasksPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [view, setView] = React.useState('Open');
  const [assignee, setAssignee] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!companyId) return;
    request.get('/users', { company_id: companyId }).then((r) => r.json()).then((r) => r.status && setUsers(r.data));
    request.get('/projects', { company_id: companyId }).then((r) => r.json()).then((r) => r.status && setProjects(r.data));
  }, [companyId]);

  const fetchTasks = () => {
    if (!companyId) return;
    setLoading(true);
    const q = { company_id: companyId };
    if (view === 'Open') q.open = '1';
    if (view === 'My tasks') {
      q.mine = '1';
      q.open = '1';
    }
    if (view === 'Overdue') q.overdue = '1';
    if (view === 'Completed') q.status = 'COMPLETED';
    if (assignee) q.assigned_user_id = assignee;
    request
      .get('/tasks', q)
      .then((r) => r.json())
      .then((r) => r.status && setRows(r.data))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchTasks, [companyId, view, assignee]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/tasks', {
          ...values,
          exp_start_date: values.exp_start_date ? values.exp_start_date.format('YYYY-MM-DD') : null,
          exp_end_date: values.exp_end_date ? values.exp_end_date.format('YYYY-MM-DD') : null,
        })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Task created' });
            setModalOpen(false);
            fetchTasks();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const columns = [
    { title: 'Task', dataIndex: 'subject', render: (v, t) => <Link to={`/tasks/${t.id}`}>{v}</Link> },
    { title: 'Project', key: 'project', render: (_, t) => t.Project?.name || '-' },
    { title: 'Client', key: 'client', render: (_, t) => (t.Project?.Client ? <Link to={`/clients/${t.Project.Client.id}`}>{t.Project.Client.client_name}</Link> : 'Own') },
    { title: 'Assigned', key: 'assigned', render: (_, t) => t.AssignedUser?.name || <Typography.Text type="secondary">unassigned</Typography.Text> },
    { title: 'Priority', dataIndex: 'priority', width: 90, render: (v) => <Tag color={PRIORITY_COLORS[v]}>{label(v)}</Tag> },
    { title: 'Start', dataIndex: 'exp_start_date', width: 110, render: (v) => v || '-' },
    { title: 'Due', key: 'due', width: 110, sorter: (a, b) => String(a.exp_end_date || '9').localeCompare(String(b.exp_end_date || '9')), render: (_, t) => <DueDate task={t} /> },
    { title: 'Checklist', key: 'checklist', width: 130, render: (_, t) => <ChecklistProgress items={t.TaskChecklistItems} /> },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={TASK_STATUS_COLORS[v]}>{label(v)}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, t) => (
        <Space size={4}>
          {t.status === 'OPEN' && <Button size="small" icon={<PlayCircleOutlined />} title="Start" onClick={() => quickStatus(t, 'WORKING', fetchTasks)} />}
          {(t.status === 'OPEN' || t.status === 'WORKING') && <Button size="small" icon={<CheckOutlined />} title="Complete" onClick={() => quickStatus(t, 'COMPLETED', fetchTasks)} />}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Tasks
        </Typography.Title>
        <Space wrap>
          {isSystemAdmin && (
            <Select style={{ width: 180 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Segmented options={VIEWS} value={view} onChange={setView} />
          <Select allowClear showSearch optionFilterProp="label" style={{ width: 180 }} placeholder="Any assignee" value={assignee} onChange={setAssignee} options={users.map((u) => ({ value: u.id, label: u.name }))} />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ priority: 'MEDIUM' });
              setModalOpen(true);
            }}
          >
            New Task
          </Button>
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 25 }} />

      <Modal title="New Task" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Typography.Paragraph type="secondary">Generated tasks come from activating a Service Engagement; use this for ad-hoc work on an existing project.</Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="project_id" label="Project" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={projects.map((p) => ({ value: p.id, label: p.name }))} />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="assigned_user_id" label="Assign to" style={{ width: 230 }}>
              <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name }))} />
            </Form.Item>
            <Form.Item name="priority" label="Priority" style={{ width: 130 }}>
              <Select options={['LOW', 'MEDIUM', 'HIGH'].map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="exp_start_date" label="Start" style={{ width: 180 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="exp_end_date" label="Due" style={{ width: 180 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
